import { Alert, Button, Group, Stack } from "@mantine/core"
import { ChatHeaderGroup } from "../../../../-components/chat/ChatHeaderGroup"
import { DefaultInfiniteList } from "../../../../../-components/InfiniteList"
import { useDefaultSuspenseInfiniteQuery } from "../../../../../utils/useDefaultSuspenseInfiniteQuery"
import { FeedPostCard } from "./FeedPostCard"
import { useDisclosure } from "@mantine/hooks"
import { useGetAuthenticatedUser } from "../../../-components/use-authenticated-user"
import { FeedSubscribeIcon } from "./FeedSubscribeIcon"
import { useTranslation } from "react-i18next"
import {
  getFeedPosts,
  getGetFeedPostsQueryKey,
  useGetChatByIdSuspense,
  getGetChatByIdQueryKey,
  getGetChatsQueryKey,
} from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useToggleMuteChat } from "@/orval/gen"
import { ActionIcon } from "@mantine/core"
import { IconBell, IconBellOff } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { FeedPostFormModal } from "./FeedPostCard/FeedPostFormModal"

interface Props {
  chatId: number
}

export function FeedView({ chatId }: Props) {
  const { t } = useTranslation("app_server")
  const { data: chat } = useGetChatByIdSuspense({
    chatId: chatId,
  })

  const [
    isNewPostModalOpen,
    { open: openNewPostModal, close: closeNewPostModal },
  ] = useDisclosure()

  const queryResult = useDefaultSuspenseInfiniteQuery(
    getFeedPosts,
    (pageRequest) => ({
      chatId,
      ...pageRequest,
    }),
    { queryKey: getGetFeedPostsQueryKey({ chatId }) }
  )

  if (!chat) return <Alert color="red">{t("FeedNotFound")}</Alert>

  const currentUserId = useGetAuthenticatedUser().data.userId
  const userMember = chat.chatMembers.find((cm) => cm.userId === currentUserId)
  const userIsActivelySubscribed = !!userMember && !userMember.isInactive
  const userCanPost = !chat.isReadOnly || userMember?.isAdmin
  const queryClient = useQueryClient()
  const invalidateQueries = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getGetChatByIdQueryKey({ chatId }),
    })
    queryClient.invalidateQueries({
      queryKey: getGetChatsQueryKey(),
    })
  }, [queryClient, chatId])

  const { mutateAsync: toggleMute } = useToggleMuteChat({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => invalidateQueries(),
    },
  })

  return (
    <Stack flex="1">
      <ChatHeaderGroup
        chatId={chatId}
        rightSection={
          <Group justify="space-between">
            {chat.chatType === "Feed" && userIsActivelySubscribed && (
              <ActionIcon
                variant="subtle"
                onClick={async (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  try {
                    await toggleMute({ params: { chatId } })
                  } catch {}
                }}
              >
                {userMember?.isMuted ? <IconBellOff /> : <IconBell />}
              </ActionIcon>
            )}
            {chat.chatType === "Feed" && !userMember?.isAdmin && (
              <FeedSubscribeIcon
                subscribed={userIsActivelySubscribed}
                id={chatId}
              />
            )}
            {userCanPost && (
              <Button
                onClick={(evt) => {
                  evt.stopPropagation()
                  openNewPostModal()
                }}
              >
                {t("NewPost")}
              </Button>
            )}
          </Group>
        }
      />
      <FeedPostFormModal
        mode="create"
        chatId={chatId}
        opened={isNewPostModalOpen}
        onClose={closeNewPostModal}
      />
      <DefaultInfiniteList
        flex="1"
        selectable
        align="center"
        queryResult={queryResult}
        gap="sm"
        renderItem={({ entity }) => (
          <FeedPostCard hideComments={"collapsed"} feedPost={entity} />
        )}
      />
    </Stack>
  )
}
